import React, {
  createContext,
  useCallback,
  useState,
  type ReactNode,
} from 'react';

/**
 * Options for displaying a toast notification.
 * The basic API (via useToast hook) only exposes variant, duration, and dismissible.
 * Advanced usage via ToastContext directly can include additional fields.
 */
export interface ToastOptions {
  variant?: 'success' | 'error' | 'info' | 'warning';
  duration?: number;
  dismissible?: boolean;
  className?: string;
  title?: string | ReactNode;
  actions?: ({ dismiss }: { dismiss: () => void }) => ReactNode;
}

export interface ToastMessage extends ToastOptions {
  id: string;
  message: string | ReactNode;
}

export interface ToastContextType {
  toasts: ToastMessage[];
  addToast: (message: string | ReactNode, options?: ToastOptions) => void;
  removeToast: (id: string) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const ToastContext = createContext<ToastContextType | undefined>(
  undefined,
);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback(
    (message: string | ReactNode, options: ToastOptions = {}) => {
      // Check if a toast with the same message already exists
      setToasts((prev) => {
        const isDuplicate =
          // only check for duplicates if the message is a string
          // if it's a ReactNode, we can't easily compare
          // in the future we could add a key or id to ToastOptions to handle this better
          typeof message === 'string'
            ? prev.some((toast) => {
                // if the comparison message is not a string, skip (see above comment)
                if (typeof toast.message !== 'string') return false;

                // check both message and variant match before marking as duplicate
                return (
                  toast.message === message &&
                  toast.variant === (options.variant || 'info')
                );
              })
            : // was not a string, so mark false
              false;

        // If duplicate exists, don't add a new toast
        if (isDuplicate) {
          return prev;
        }

        const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
        const duration = options.duration ?? 5000;
        const newToast: ToastMessage = {
          id,
          message,
          variant: options.variant || 'info',
          duration,
          dismissible: options.dismissible ?? true,
        };

        // Auto-dismiss after duration
        if (duration > 0) {
          setTimeout(() => {
            removeToast(id);
          }, duration);
        }

        return [...prev, newToast];
      });
    },
    [removeToast],
  );

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
    </ToastContext.Provider>
  );
};
